import { Button, Card, Col, Form, Radio, RadioChangeEvent, Row } from "antd";
import FullPageLayout from "../../../../../templates/FullPageLayout";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { InputField } from "../../../../../atoms/FormFields";
import {
  ConstructionIcon,
  EnhancementsIcon,
  HolidayLightingIcon,
  IrrigationIcon,
  LandscapeIcon,
  LawnCareIcon,
  SnowServiceIcon,
  TreeServiceIcon,
} from "../../../../../atoms/CustomIcons";
import { useLocation, useNavigate } from "react-router-dom";
import { createServiceLine } from "../../ducks/services";
import SuccessMessage from "../../../../../atoms/Toasts/SuccessMessage";
import { AxiosError, AxiosResponse } from "axios";
import ErrorMessage from "../../../../../atoms/Toasts/ErrorMessage";

interface FormValues {
  name: string;
  address: string;
}

export default function Custom() {
  const [loading, setLoading] = useState(false);
  const [color, setColor] = useState("mint_color");
  const [serviceIcon, setServiceIcon] = useState("LandscapeIcon");
  const navigate = useNavigate();
  const url = useLocation();
  const companyId = url?.pathname.split("/")[3];

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = (val: FormValues) => {
    setLoading(true);

    const payload = {
      name: val?.name ?? "",
      color: color,
      company_id: companyId,
      icon: serviceIcon,
    };

    createServiceLine(payload)
      .then((res: AxiosResponse) => {
        setLoading(false);
        SuccessMessage("Service line created");
        navigate(
          `/company-settings/service-lines/${res?.data?.id}/${companyId}`
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const handleRadioIconChange = (e: RadioChangeEvent) => {
    setServiceIcon(e.target.value);
  };

  const handleRadioColorChange = (e: RadioChangeEvent) => {
    setColor(e.target.value);
  };

  return (
    <FullPageLayout loading={loading}>
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Col style={{ width: "672px", paddingBottom: 85 }}>
          <div className="title">
            <h3 style={{ marginBottom: 8 }}> Custom service line details </h3>
            <p>Complete the details of your custom service line.</p>
          </div>

          <Card>
            <h4 style={{ marginBottom: "16px" }}> Service line details </h4>

            <Row>
              <Col span={24}>
                <InputField
                  fieldName="name"
                  label="Name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter service line name",
                  }}
                  isRequired
                  rules={{
                    required: "Service line name is required.",
                  }}
                  validate={errors.name && "error"}
                  validMessage={errors.name && errors.name.message}
                />
              </Col>

              <Col span={13} className="custom_service_radio_color">
                <h6 className="radioTitle"> Choose color: </h6>
                <Radio.Group
                  onChange={handleRadioColorChange}
                  name="radiogroup"
                  value={color}
                  buttonStyle="outline"
                  options={[
                    { value: "pink_color", label: "" },
                    { value: "purple_color", label: "" },
                    { value: "violet_color", label: "" },
                    { value: "blue_color", label: "" },
                    { value: "mint_color", label: "" },
                    { value: "yellow_color", label: "" },
                    { value: "orange_color", label: "" },
                    { value: "red_color", label: "" },
                  ]}
                />
              </Col>

              <Col span={11} className="custom_service_radio_icon">
                <h6 className="radioTitle">Choose icon:</h6>
                <Radio.Group
                  onChange={handleRadioIconChange}
                  value={serviceIcon}
                >
                  <div className="grouping">
                    <Radio.Button
                      value="LandscapeIcon"
                      className="icon_selected_mint_color"
                    >
                      <LandscapeIcon className="defaultColor" />
                    </Radio.Button>
                    <Radio.Button
                      value="EnhancementsIcon"
                      className="icon_selected_red_color"
                    >
                      <EnhancementsIcon className="defaultColor" />
                    </Radio.Button>
                    <Radio.Button
                      value="LawnCareIcon"
                      className="icon_selected_orange_color"
                    >
                      <LawnCareIcon className="defaultColor" />
                    </Radio.Button>
                    <Radio.Button
                      value="IrrigationIcon"
                      className="icon_selected_violet_color"
                    >
                      <IrrigationIcon className="defaultColor" />
                    </Radio.Button>
                  </div>
                  <div className="grouping">
                    <Radio.Button
                      value="ConstructionIcon"
                      className="icon_selected_yellow_color"
                    >
                      <ConstructionIcon className="defaultColor" />
                    </Radio.Button>
                    <Radio.Button
                      value="HolidayLightingIcon"
                      className="icon_selected_blue_color"
                    >
                      <HolidayLightingIcon className="defaultColor" />
                    </Radio.Button>
                    <Radio.Button
                      value="SnowServiceIcon"
                      className="icon_selected_purple_color"
                    >
                      <SnowServiceIcon className="defaultColor" />
                    </Radio.Button>
                    <Radio.Button
                      value="TreeServiceIcon"
                      className="icon_selected_green_color"
                    >
                      <TreeServiceIcon className="defaultColor" />
                    </Radio.Button>
                  </div>
                </Radio.Group>
              </Col>
            </Row>
          </Card>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() =>
                  navigate(`/company-settings/create-service-line/${companyId}`)
                }
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "16px" }}>
                <Button htmlType="submit" type="primary" className="mint-btn">
                  Create service line
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
